import React, { useCallback, useEffect } from 'react';

import type { ClassificationOption } from '@atlassian/data-classification-level';

import { LoadableLazy } from '@confluence/loadable';

import type { ContentClassificationMetadata } from '../hooks/useContentClassification.ts';
import {
	useClassificationModalContext,
	useClassificationModalDispatchContext,
} from '../contexts/ClassificationModalContext.tsx';
import { useEditSubmitCallback } from '../hooks/useEditSubmitCallback.ts';
import {
	ActionSubjectId,
	useTrackClassificationAnalytics,
} from '../hooks/useTrackClassificationAnalytics.ts';

const LazyClassificationEditModalContent = LoadableLazy({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-ClassificationEditModalContent" */ './ClassificationEditModalContent.tsx'
			)
		).ClassificationEditModalContent,
});

type ClassificationEditModalProps = {
	contentId: string;
	spaceKey: string;
	contentClassificationMetadata: ContentClassificationMetadata;
};

export const ClassificationEditModal = ({
	contentId,
	spaceKey,
	contentClassificationMetadata,
}: ClassificationEditModalProps) => {
	const { isClassificationModalOpen } = useClassificationModalContext();
	const { setIsClassificationModalOpen } = useClassificationModalDispatchContext();
	const { trackEditPopupViewed } = useTrackClassificationAnalytics();

	const handleSubmit = useEditSubmitCallback({
		contentId,
		contentClassificationMetadata,
	});

	const { classification, spaceDefaultIsOverridden } = contentClassificationMetadata;

	const defaultSelectedEditOption = spaceDefaultIsOverridden ? classification : undefined;

	const onEditSubmit = useCallback(
		(classificationOption: ClassificationOption) => {
			handleSubmit(classificationOption);
			setIsClassificationModalOpen(false);
		},
		[handleSubmit, setIsClassificationModalOpen],
	);

	const onEditCancel = useCallback(() => {
		setIsClassificationModalOpen(false);
	}, [setIsClassificationModalOpen]);

	useEffect(() => {
		if (isClassificationModalOpen) {
			trackEditPopupViewed(ActionSubjectId.GENERIC_DATA_CLASSIFICATION_MODAL_POPUP);
		}
	}, [isClassificationModalOpen, trackEditPopupViewed]);

	if (!isClassificationModalOpen) {
		return null;
	}

	return (
		<LazyClassificationEditModalContent
			onClose={() => setIsClassificationModalOpen(false)}
			onEditSubmit={onEditSubmit}
			onEditCancel={onEditCancel}
			selectedOption={defaultSelectedEditOption ?? undefined}
			spaceKey={spaceKey}
			contentId={contentId}
		/>
	);
};
